import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ClientIInfo } from '../../../../../core/models/parking.model';
import {
  CardAdvanceDetail,
  FinanceForCardAdvanceSearch,
  StandardAdvanceConfigOption,
} from '../../../../../core/models/card-quota/card-advance.model';
import { MapperCardAdvanceService } from '../../../../../core/mappers/card-quota/mapper-card-advance.service';
import { ApiCardQuotaService } from '../../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { isDateRangeValid } from '../../../../../core/utils/date-input.util';

@Component({
  selector: 'app-modal-card-advance',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-card-advance.component.html',
  styleUrl: './modal-card-advance.component.css',
})
export class ModalCardAdvanceComponent implements OnInit {
  private static readonly MAX_QTY = 500;

  readonly wizardSteps = [
    { id: 1, title: 'เลือกร้าน', hint: 'ค้นหาและเลือกร้าน / บริษัท' },
    { id: 2, title: 'รายละเอียดบัตร', hint: 'Subscription จำนวน และวันที่' },
    { id: 3, title: 'ยืนยัน', hint: 'แนบใบเสร็จ (ถ้ามี) แล้วบันทึก' },
  ] as const;

  @Input() advanceId = 0;
  @Input() siteId = 0;
  @Input() initialClientId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  formData!: CardAdvanceDetail;
  clients: ClientIInfo[] = [];
  filteredClientOptions: ClientIInfo[] = [];
  clientSearchText = '';
  subscriptionOptions: StandardAdvanceConfigOption[] = [];
  selectedConfigId = 0;
  financeReceiptSearch = '';
  financeOptions: FinanceForCardAdvanceSearch[] = [];
  selectedFinance: FinanceForCardAdvanceSearch | null = null;
  hasSearchedFinance = false;

  wizardStep = signal(1);
  isSaving = signal(false);
  isLoadingDetail = signal(false);
  isLoadingSubscriptionOptions = signal(false);
  isSearchingFinance = signal(false);

  private readonly api = inject(ApiCardQuotaService);
  private readonly clientScope = inject(ClientAccessScopeService);
  private readonly mapper = inject(MapperCardAdvanceService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get currentStepMeta() {
    return this.wizardSteps.find((s) => s.id === this.wizardStep()) ?? this.wizardSteps[0];
  }

  get isClientFieldLocked(): boolean {
    return this.clientScope.isClientLocked();
  }

  get canGoNext(): boolean {
    const step = this.wizardStep();
    if (step === 1) return this.formData.clientId > 0;
    if (step === 2) {
      return (
        this.formData.subscriptionId > 0 &&
        this.formData.vehicleTypeId > 0 &&
        this.isQtyValid &&
        isDateRangeValid(this.formData.startDate, this.formData.endDate)
      );
    }
    return true;
  }

  get canSave(): boolean {
    if (this.isSaving()) return false;
    if (!this.formData.clientId || !this.formData.subscriptionId || !this.formData.vehicleTypeId) return false;
    if (!this.isQtyValid) return false;
    if (this.financeReceiptSearch.trim() && !this.selectedFinance) return false;
    return isDateRangeValid(this.formData.startDate, this.formData.endDate);
  }

  cardScopeLabel = (scope: string) => this.mapper.cardScopeLabel(scope);

  get isQtyValid(): boolean {
    return this.formData.qty >= 1 && this.formData.qty <= ModalCardAdvanceComponent.MAX_QTY;
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyForm(this.siteId);
    this.loadClients();
    this.loadSubscriptionOptions();
    if (this.advanceId > 0) {
      this.loadDetail(this.advanceId);
    } else if (this.initialClientId > 0) {
      this.formData.clientId = this.initialClientId;
      this.onClientSelected(this.initialClientId);
      this.wizardStep.set(2);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  goStep(step: number): void {
    if (step < 1 || step > this.wizardSteps.length) return;
    if (step > this.wizardStep() && !this.validateStep(this.wizardStep(), false)) return;
    this.wizardStep.set(step);
  }

  nextStep(): void {
    if (!this.validateStep(this.wizardStep(), true)) return;
    if (this.wizardStep() < this.wizardSteps.length) {
      this.wizardStep.update((s) => s + 1);
    }
  }

  prevStep(): void {
    if (this.wizardStep() > 1) {
      this.wizardStep.update((s) => s - 1);
    }
  }

  onSaveDraft(): void {
    this.save(false);
  }

  onSubmitApproval(): void {
    this.save(true);
  }

  onClientListSearch(text: string): void {
    this.clientSearchText = text;
    this.applyClientFilter();
  }

  onClientListSelect(client: ClientIInfo): void {
    this.onClientSelected(client.id, client.name);
  }

  selectSubscription(configId: number): void {
    this.selectedConfigId = configId;
    this.onSubscriptionChange();
  }

  onSubscriptionChange(): void {
    if (!this.selectedConfigId) {
      this.clearCardConfigFields();
      return;
    }
    this.applySelectedConfig();
  }

  private clearCardConfigFields(): void {
    this.formData.cardTypeId = 0;
    this.formData.cardTypeName = '';
    this.formData.subscriptionId = 0;
    this.formData.subscriptionName = '';
    this.formData.vehicleTypeId = 0;
    this.formData.vehicleTypeName = '';
    this.formData.cardScope = '';
  }

  onSearchFinance(): void {
    const receipt = this.financeReceiptSearch.trim();
    if (receipt.length < 2) {
      this.toast.error('กรุณาระบุเลขที่ใบเสร็จอย่างน้อย 2 ตัวอักษร');
      return;
    }

    this.isSearchingFinance.set(true);
    this.hasSearchedFinance = true;
    this.financeOptions = [];
    this.selectedFinance = null;

    this.api.searchFinanceForCardAdvance(this.siteId, receipt).subscribe({
      next: (res) => {
        this.financeOptions = this.mapper.mapFinanceSearch(res ?? []);
        this.isSearchingFinance.set(false);
        if (this.financeOptions.length === 0) {
          this.toast.error('ไม่พบใบเสร็จ — ลองค้นหาใหม่');
        }
      },
      error: (err) => {
        this.isSearchingFinance.set(false);
        this.toast.error(err?.error?.message || 'ค้นหาใบเสร็จล้มเหลว');
      },
    });
  }

  onSelectFinance(item: FinanceForCardAdvanceSearch): void {
    this.selectedFinance = item;
    this.formData.financeId = item.id;
    this.formData.financeReceiptId = item.receiptId;
  }

  onClearFinance(): void {
    this.selectedFinance = null;
    this.financeOptions = [];
    this.financeReceiptSearch = '';
    this.hasSearchedFinance = false;
    this.formData.financeId = null;
    this.formData.financeReceiptId = null;
  }

  isFinanceSelected(item: FinanceForCardAdvanceSearch): boolean {
    return this.selectedFinance?.id === item.id;
  }

  onQtyInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    if (raw === '' || raw === '-') return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    if (parsed > ModalCardAdvanceComponent.MAX_QTY) {
      this.formData.qty = ModalCardAdvanceComponent.MAX_QTY;
      (event.target as HTMLInputElement).value = String(ModalCardAdvanceComponent.MAX_QTY);
    }
  }

  private validateStep(step: number, showToast: boolean): boolean {
    if (step === 1) {
      if (!this.formData.clientId) {
        if (showToast) this.toast.error('กรุณาเลือกร้าน');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!this.formData.subscriptionId) {
        if (showToast) this.toast.error('กรุณาเลือก Subscription');
        return false;
      }
      if (!this.formData.vehicleTypeId) {
        if (showToast) this.toast.error('Subscription ที่เลือกไม่มีประเภทรถ');
        return false;
      }
      if (!this.isQtyValid) {
        if (showToast) this.toast.error(`จำนวนต้องอยู่ระหว่าง 1 ถึง ${ModalCardAdvanceComponent.MAX_QTY}`);
        return false;
      }
      if (!isDateRangeValid(this.formData.startDate, this.formData.endDate)) {
        if (showToast) this.toast.error('วันหมดอายุต้องไม่น้อยกว่าวันเริ่มต้น');
        return false;
      }
      return true;
    }
    return true;
  }

  private onClientSelected(clientId: number, clientName?: string): void {
    this.formData.clientId = clientId;
    const client = this.clients.find((c) => c.id === clientId);
    this.formData.clientName = clientName ?? client?.name ?? '';
  }

  private save(submitForApproval: boolean): void {
    const isEdit = this.advanceId > 0;
    if (isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    if (!this.validateStep(1, true) || !this.validateStep(2, true)) return;

    if (this.financeReceiptSearch.trim() && !this.selectedFinance) {
      this.toast.error('กรุณาค้นหาและเลือกใบเสร็จจากรายการ');
      return;
    }

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url);
    const financeId = this.selectedFinance?.id ?? null;
    const payload = this.mapper.buildSavePayload(this.formData, userId, menuId, submitForApproval, financeId);

    this.isSaving.set(true);
    this.api.saveCardAdvance(payload).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        const requiresApproval = Boolean(
          res.requiresApproval ?? (res as unknown as Record<string, unknown>)['RequiresApproval']
        );
        if (submitForApproval) {
          this.toast.success(requiresApproval ? 'ส่งอนุมัติสำเร็จ' : 'อนุมัติอัตโนมัติ — พร้อม Gen Code');
        } else {
          this.toast.success('บันทึก draft สำเร็จ');
        }
        this.saveSuccess.emit();
        this.onClose();
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'บันทึกล้มเหลว');
      },
    });
  }

  private loadDetail(id: number): void {
    this.isLoadingDetail.set(true);
    this.api.getCardAdvanceById(id).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapDetail(res as CardAdvanceDetail & Record<string, unknown>);
        if (this.formData.financeId && this.formData.financeReceiptId) {
          this.selectedFinance = {
            id: this.formData.financeId,
            receiptId: this.formData.financeReceiptId,
            paidDate: null,
            fee: 0,
            vat: 0,
            total: 0,
          };
          this.financeReceiptSearch = this.formData.financeReceiptId;
        }
        this.isLoadingDetail.set(false);
        this.wizardStep.set(2);
        this.loadSubscriptionOptions(this.formData.subscriptionId, this.formData.cardTypeId);
      },
      error: () => {
        this.isLoadingDetail.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  private loadClients(): void {
    const userId = Number(this.cookieService.get('user_id') || 0);
    if (!this.siteId || !userId) {
      this.clients = [];
      this.filteredClientOptions = [];
      return;
    }

    this.clientScope.load(this.siteId, userId).subscribe({
      next: (state) => {
        this.clients = state.clients;
        if (this.formData.clientId > 0 && this.advanceId === 0) {
          this.onClientSelected(this.formData.clientId);
        } else if (this.isClientFieldLocked && this.clients.length === 1 && this.advanceId === 0) {
          this.onClientSelected(this.clients[0].id);
          this.wizardStep.set(2);
        }
        this.applyClientFilter();
      },
      error: () => {
        this.clients = [];
        this.filteredClientOptions = [];
      },
    });
  }

  private applyClientFilter(): void {
    const sorted = [...this.clients].sort((a, b) => a.name.localeCompare(b.name, 'th'));
    const q = this.clientSearchText.trim().toLowerCase();
    this.filteredClientOptions = q
      ? sorted.filter((c) => c.name.toLowerCase().includes(q) || String(c.id).includes(q))
      : sorted;
  }

  private loadSubscriptionOptions(preselectSubscriptionId?: number, preselectCardTypeId?: number): void {
    if (!this.siteId) {
      this.subscriptionOptions = [];
      return;
    }

    this.isLoadingSubscriptionOptions.set(true);
    this.api.getStandardAdvanceConfigOptions(this.siteId).subscribe({
      next: (res) => {
        this.subscriptionOptions = this.mapper.mapConfigOptions(res ?? []);
        this.isLoadingSubscriptionOptions.set(false);

        if (this.subscriptionOptions.length === 0) {
          this.toast.error('ไม่พบ Subscription จอดฟรีใน Access Group / ลานนี้');
          return;
        }

        const preselected = this.subscriptionOptions.find(
          (o) =>
            (preselectSubscriptionId ?? 0) > 0 &&
            o.subscriptionId === preselectSubscriptionId &&
            ((preselectCardTypeId ?? 0) <= 0 || o.cardTypeId === preselectCardTypeId)
        );

        if (preselected) {
          this.selectedConfigId = preselected.configId;
        } else if (this.subscriptionOptions.length === 1 && this.advanceId === 0) {
          this.selectedConfigId = this.subscriptionOptions[0].configId;
        }

        if (this.selectedConfigId !== 0) {
          this.applySelectedConfig();
        }
      },
      error: (err) => {
        this.subscriptionOptions = [];
        this.isLoadingSubscriptionOptions.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายการ subscription ล้มเหลว');
      },
    });
  }

  private applySelectedConfig(): void {
    const option = this.subscriptionOptions.find((o) => o.configId === this.selectedConfigId);
    if (!option) return;

    this.formData.subscriptionId = option.subscriptionId;
    this.formData.cardTypeId = option.cardTypeId;
    this.formData.cardTypeName = option.cardTypeName;
    this.formData.subscriptionName = option.subscriptionName;
    this.formData.vehicleTypeId = option.vehicleTypeId;
    this.formData.vehicleTypeName = option.vehicleTypeName;
    this.formData.cardScope = option.cardScope;
  }
}
